import React, { Component } from 'react';

// ── Per-project illustrated scenes (custom SVG) ─────────────────
// Each scene fills the visual panel of its project card in place of
// the fallback icon plate. Scenes intentionally share palette and
// stroke weights so the set reads as one system.

function EducationPlatformScene() {
    return (
        <svg
            viewBox="0 0 400 300"
            className="relative w-full max-w-[340px] h-auto"
            aria-hidden="true"
        >
            <defs>
                <linearGradient id="pd-cert" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#22304f" />
                    <stop offset="100%" stopColor="#151f33" />
                </linearGradient>
                <linearGradient id="pd-cap" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#22304f" />
                    <stop offset="100%" stopColor="#0f1626" />
                </linearGradient>
                <radialGradient id="pd-halo" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="rgba(33,194,200,0.45)" />
                    <stop offset="100%" stopColor="rgba(33,194,200,0)" />
                </radialGradient>
            </defs>

            {/* Certificate — landscape orientation */}
            <g transform="translate(80 70)">
                <rect width="240" height="170" rx="6" fill="url(#pd-cert)" stroke="rgba(33,194,200,0.4)" strokeWidth="1.25" />
                <rect x="12" y="12" width="216" height="146" rx="3" fill="none" stroke="rgba(33,194,200,0.22)" strokeWidth="0.75" strokeDasharray="3 2" />

                {/* Corner ornaments */}
                <g fill="rgba(33,194,200,0.35)">
                    <path d="M 12 12 L 24 12 L 12 24 Z" />
                    <path d="M 228 12 L 216 12 L 228 24 Z" />
                    <path d="M 12 158 L 24 158 L 12 146 Z" />
                    <path d="M 228 158 L 216 158 L 228 146 Z" />
                </g>

                {/* Title area */}
                <rect x="60" y="34" width="120" height="6" rx="2" fill="#21c2c8" />
                <rect x="80" y="47" width="80" height="3" rx="1.5" fill="rgba(255,255,255,0.3)" />

                {/* Body text lines */}
                <g fill="rgba(255,255,255,0.2)">
                    <rect x="45" y="72" width="150" height="2.5" rx="1" />
                    <rect x="60" y="82" width="120" height="2.5" rx="1" />
                    <rect x="45" y="92" width="150" height="2.5" rx="1" />
                </g>

                {/* Signature line */}
                <line x1="50" y1="132" x2="130" y2="132" stroke="rgba(255,255,255,0.28)" strokeWidth="1" />
                <rect x="60" y="137" width="60" height="2" rx="1" fill="rgba(255,255,255,0.2)" />

                {/* Approval seal */}
                <g transform="translate(180 132)">
                    <circle cx="0" cy="0" r="26" fill="url(#pd-halo)" />
                    <circle cx="0" cy="0" r="16" fill="#21c2c8" stroke="rgba(160,245,250,0.5)" strokeWidth="1.25" />
                    <circle cx="0" cy="0" r="12" fill="none" stroke="rgba(11,18,32,0.35)" strokeWidth="0.75" strokeDasharray="1.5 2" />
                    <path d="M 0 -7 L 1.8 -2.2 L 7 -2 L 3 1.5 L 4.4 6.6 L 0 3.6 L -4.4 6.6 L -3 1.5 L -7 -2 L -1.8 -2.2 Z" fill="#0b1220" />
                </g>
            </g>

            {/* Graduation cap — perched at top-right of certificate */}
            <g transform="translate(305 60) rotate(15)">
                <ellipse cx="0" cy="8" rx="42" ry="14" fill="url(#pd-halo)" />

                {/* Cap band */}
                <path d="M -20 0 L 20 0 L 17 12 L -17 12 Z" fill="url(#pd-cap)" stroke="rgba(33,194,200,0.5)" strokeWidth="1" />
                <line x1="-17" y1="6" x2="17" y2="6" stroke="rgba(33,194,200,0.35)" strokeWidth="0.75" />

                {/* Mortarboard */}
                <path d="M 0 -14 L 30 -3 L 0 6 L -30 -3 Z" fill="url(#pd-cap)" stroke="rgba(33,194,200,0.6)" strokeWidth="1.25" />
                <circle cx="0" cy="-3.5" r="2.5" fill="#21c2c8" />

                {/* Tassel */}
                <path d="M 0 -3.5 L 18 -6 L 20 14" stroke="rgba(33,194,200,0.7)" strokeWidth="1.25" fill="none" strokeLinecap="round" />
                <circle cx="20" cy="14" r="3" fill="#21c2c8" />
            </g>
        </svg>
    );
}

function FreightPricingScene() {
    return (
        <svg
            viewBox="0 0 400 300"
            className="relative w-full max-w-[340px] h-auto"
            aria-hidden="true"
        >
            <defs>
                <linearGradient id="ak-truck" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#22304f" />
                    <stop offset="100%" stopColor="#0f1626" />
                </linearGradient>
                <radialGradient id="ak-halo" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="rgba(33,194,200,0.45)" />
                    <stop offset="100%" stopColor="rgba(33,194,200,0)" />
                </radialGradient>
                <linearGradient id="ak-route" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="rgba(33,194,200,0.08)" />
                    <stop offset="50%" stopColor="rgba(33,194,200,0.6)" />
                    <stop offset="100%" stopColor="rgba(33,194,200,0.08)" />
                </linearGradient>
            </defs>

            {/* Route curve */}
            <path
                d="M 25 235 Q 110 155 210 200 T 380 130"
                fill="none"
                stroke="url(#ak-route)"
                strokeWidth="2.5"
                strokeDasharray="6 5"
                strokeLinecap="round"
            />
            {/* Route waypoints */}
            <circle cx="25" cy="235" r="4" fill="#21c2c8" />
            <circle cx="380" cy="130" r="4" fill="#21c2c8" />

            {/* Semi-truck silhouette — side profile, heading right */}
            <g transform="translate(160 155)">
                {/* Trailer */}
                <rect x="-100" y="-30" width="130" height="40" rx="3" fill="url(#ak-truck)" stroke="rgba(33,194,200,0.5)" strokeWidth="1.25" />
                <line x1="-95" y1="-12" x2="25" y2="-12" stroke="rgba(33,194,200,0.18)" strokeWidth="0.5" />
                <line x1="-95" y1="-2" x2="25" y2="-2" stroke="rgba(33,194,200,0.18)" strokeWidth="0.5" />

                {/* Cab */}
                <path d="M 30 -30 L 60 -30 L 68 -15 L 68 10 L 30 10 Z" fill="url(#ak-truck)" stroke="rgba(33,194,200,0.55)" strokeWidth="1.25" />
                {/* Cab window */}
                <path d="M 42 -25 L 58 -25 L 64 -15 L 42 -15 Z" fill="#21c2c8" opacity="0.4" />

                {/* Wheels */}
                <g>
                    <circle cx="-70" cy="14" r="7.5" fill="#0b1220" stroke="rgba(33,194,200,0.5)" strokeWidth="1.25" />
                    <circle cx="-50" cy="14" r="7.5" fill="#0b1220" stroke="rgba(33,194,200,0.5)" strokeWidth="1.25" />
                    <circle cx="55" cy="14" r="7.5" fill="#0b1220" stroke="rgba(33,194,200,0.5)" strokeWidth="1.25" />
                    <circle cx="-70" cy="14" r="2.5" fill="rgba(33,194,200,0.55)" />
                    <circle cx="-50" cy="14" r="2.5" fill="rgba(33,194,200,0.55)" />
                    <circle cx="55" cy="14" r="2.5" fill="rgba(33,194,200,0.55)" />
                </g>
            </g>

            {/* Fuel gauge — top-right overlay */}
            <g transform="translate(310 75)">
                <circle cx="0" cy="0" r="42" fill="url(#ak-halo)" />
                <circle cx="0" cy="0" r="28" fill="#151f33" stroke="rgba(33,194,200,0.5)" strokeWidth="1.5" />

                {/* Gauge arc */}
                <path d="M -19 10 A 22 22 0 1 1 19 10" fill="none" stroke="rgba(33,194,200,0.55)" strokeWidth="1.5" strokeLinecap="round" />
                {/* Tick marks */}
                <line x1="-19" y1="10" x2="-15" y2="6" stroke="rgba(33,194,200,0.6)" strokeWidth="1.25" />
                <line x1="0" y1="-22" x2="0" y2="-17" stroke="rgba(33,194,200,0.6)" strokeWidth="1.25" />
                <line x1="19" y1="10" x2="15" y2="6" stroke="rgba(33,194,200,0.6)" strokeWidth="1.25" />
                {/* Needle — points to upper-right (higher fuel/price signal) */}
                <line x1="0" y1="0" x2="13" y2="-13" stroke="#21c2c8" strokeWidth="2" strokeLinecap="round" />
                <circle cx="0" cy="0" r="3" fill="#21c2c8" />
                {/* Fuel drop marker below */}
                <path d="M 0 16 C -4 20 -4 24 0 26 C 4 24 4 20 0 16 Z" fill="rgba(33,194,200,0.7)" />
            </g>
        </svg>
    );
}

function FreelanceScene() {
    return (
        <svg
            viewBox="0 0 400 300"
            className="relative w-full max-w-[340px] h-auto"
            aria-hidden="true"
        >
            <defs>
                <linearGradient id="fr-box" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#22304f" />
                    <stop offset="100%" stopColor="#151f33" />
                </linearGradient>
                <linearGradient id="fr-out" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1a2540" />
                    <stop offset="100%" stopColor="#0f1626" />
                </linearGradient>
                <radialGradient id="fr-halo" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="rgba(33,194,200,0.4)" />
                    <stop offset="100%" stopColor="rgba(33,194,200,0)" />
                </radialGradient>
            </defs>

            {/* Input 1: PDF-shape (top) */}
            <g transform="translate(35 65)">
                <rect x="0" y="0" width="55" height="65" rx="3" fill="url(#fr-box)" stroke="rgba(33,194,200,0.35)" strokeWidth="1" />
                <path d="M 38 0 L 55 17 L 38 17 Z" fill="rgba(33,194,200,0.18)" />
                <line x1="38" y1="0" x2="38" y2="17" stroke="rgba(33,194,200,0.35)" strokeWidth="1" />
                <line x1="38" y1="17" x2="55" y2="17" stroke="rgba(33,194,200,0.35)" strokeWidth="1" />
                <g fill="rgba(255,255,255,0.18)">
                    <rect x="7" y="28" width="30" height="2.5" rx="1" />
                    <rect x="7" y="36" width="38" height="2.5" rx="1" />
                    <rect x="7" y="44" width="26" height="2.5" rx="1" />
                    <rect x="7" y="52" width="34" height="2.5" rx="1" />
                </g>
            </g>

            {/* Input 2: browser/website (middle) */}
            <g transform="translate(35 148)">
                <rect x="0" y="0" width="55" height="42" rx="3" fill="url(#fr-box)" stroke="rgba(33,194,200,0.35)" strokeWidth="1" />
                <rect x="0" y="0" width="55" height="10" rx="3" fill="rgba(33,194,200,0.18)" />
                <rect x="0" y="7" width="55" height="3" fill="rgba(33,194,200,0.18)" />
                <circle cx="6" cy="5" r="1.3" fill="rgba(33,194,200,0.6)" />
                <circle cx="11" cy="5" r="1.3" fill="rgba(33,194,200,0.6)" />
                <circle cx="16" cy="5" r="1.3" fill="rgba(33,194,200,0.6)" />
                <g fill="rgba(255,255,255,0.18)">
                    <rect x="7" y="18" width="25" height="2.5" rx="1" />
                    <rect x="7" y="26" width="38" height="2.5" rx="1" />
                    <rect x="7" y="34" width="30" height="2.5" rx="1" />
                </g>
            </g>

            {/* Input 3: spreadsheet grid (bottom) */}
            <g transform="translate(35 208)">
                <rect x="0" y="0" width="55" height="42" rx="3" fill="url(#fr-box)" stroke="rgba(33,194,200,0.35)" strokeWidth="1" />
                <g stroke="rgba(33,194,200,0.2)" strokeWidth="0.5">
                    <line x1="0" y1="12" x2="55" y2="12" />
                    <line x1="0" y1="24" x2="55" y2="24" />
                    <line x1="0" y1="36" x2="55" y2="36" />
                    <line x1="18" y1="0" x2="18" y2="42" />
                    <line x1="37" y1="0" x2="37" y2="42" />
                </g>
                <rect x="1" y="1" width="16" height="10" fill="rgba(33,194,200,0.22)" />
                <rect x="19" y="13" width="17" height="10" fill="rgba(33,194,200,0.15)" />
                <rect x="1" y="25" width="16" height="10" fill="rgba(33,194,200,0.1)" />
                <rect x="38" y="25" width="16" height="10" fill="rgba(33,194,200,0.18)" />
            </g>

            {/* Flow lines from inputs to central processor */}
            <g stroke="rgba(33,194,200,0.4)" strokeWidth="1.25" fill="none" strokeLinecap="round" strokeDasharray="4 3">
                <path d="M 95 97 Q 145 100 175 145" />
                <path d="M 95 170 L 175 158" />
                <path d="M 95 228 Q 145 220 175 175" />
            </g>

            {/* Central processor */}
            <g transform="translate(205 160)">
                <circle cx="0" cy="0" r="42" fill="url(#fr-halo)" />
                <circle cx="0" cy="0" r="28" fill="#151f33" stroke="rgba(33,194,200,0.55)" strokeWidth="1.5" />
                {/* Inner processor "brackets" */}
                <g stroke="rgba(33,194,200,0.7)" strokeWidth="1.5" strokeLinecap="round" fill="none">
                    <path d="M -13 -13 L -6 -13 L -6 -6" />
                    <path d="M 13 -13 L 6 -13 L 6 -6" />
                    <path d="M -13 13 L -6 13 L -6 6" />
                    <path d="M 13 13 L 6 13 L 6 6" />
                </g>
                <circle cx="0" cy="0" r="4" fill="#21c2c8" />
            </g>

            {/* Arrow to output */}
            <g stroke="rgba(33,194,200,0.6)" strokeWidth="1.5" fill="none" strokeLinecap="round">
                <path d="M 240 160 L 288 160" strokeDasharray="4 3" />
                <path d="M 283 155 L 289 160 L 283 165" strokeDasharray="0" />
            </g>

            {/* Output — clean structured table */}
            <g transform="translate(295 90)">
                <rect x="0" y="0" width="82" height="140" rx="4" fill="url(#fr-out)" stroke="rgba(33,194,200,0.5)" strokeWidth="1.25" />
                {/* Header row */}
                <rect x="0" y="0" width="82" height="18" rx="4" fill="rgba(33,194,200,0.25)" />
                <rect x="0" y="14" width="82" height="4" fill="#151f33" />
                <g fill="#21c2c8">
                    <rect x="6" y="6" width="18" height="4" rx="1" />
                    <rect x="30" y="6" width="18" height="4" rx="1" />
                    <rect x="54" y="6" width="22" height="4" rx="1" />
                </g>

                {/* Data rows */}
                <g fill="rgba(255,255,255,0.22)">
                    <rect x="6" y="25" width="18" height="2.5" rx="1" />
                    <rect x="30" y="25" width="18" height="2.5" rx="1" />
                    <rect x="54" y="25" width="22" height="2.5" rx="1" />

                    <rect x="6" y="37" width="18" height="2.5" rx="1" />
                    <rect x="30" y="37" width="18" height="2.5" rx="1" />
                    <rect x="54" y="37" width="22" height="2.5" rx="1" />

                    <rect x="6" y="49" width="18" height="2.5" rx="1" />
                    <rect x="30" y="49" width="18" height="2.5" rx="1" />
                    <rect x="54" y="49" width="22" height="2.5" rx="1" />

                    <rect x="6" y="61" width="18" height="2.5" rx="1" />
                    <rect x="30" y="61" width="18" height="2.5" rx="1" />
                    <rect x="54" y="61" width="22" height="2.5" rx="1" />

                    <rect x="6" y="73" width="18" height="2.5" rx="1" />
                    <rect x="30" y="73" width="18" height="2.5" rx="1" />
                    <rect x="54" y="73" width="22" height="2.5" rx="1" />

                    <rect x="6" y="85" width="18" height="2.5" rx="1" />
                    <rect x="30" y="85" width="18" height="2.5" rx="1" />
                    <rect x="54" y="85" width="22" height="2.5" rx="1" />

                    <rect x="6" y="97" width="18" height="2.5" rx="1" />
                    <rect x="30" y="97" width="18" height="2.5" rx="1" />
                    <rect x="54" y="97" width="22" height="2.5" rx="1" />

                    <rect x="6" y="109" width="18" height="2.5" rx="1" />
                    <rect x="30" y="109" width="18" height="2.5" rx="1" />
                    <rect x="54" y="109" width="22" height="2.5" rx="1" />

                    <rect x="6" y="121" width="18" height="2.5" rx="1" />
                    <rect x="30" y="121" width="18" height="2.5" rx="1" />
                    <rect x="54" y="121" width="22" height="2.5" rx="1" />
                </g>
            </g>
        </svg>
    );
}

function PharmacyLicenseScene() {
    return (
        <svg
            viewBox="0 0 400 300"
            className="relative w-full max-w-[340px] h-auto"
            aria-hidden="true"
        >
            <defs>
                <linearGradient id="pf-doc" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1a2540" />
                    <stop offset="100%" stopColor="#0f1626" />
                </linearGradient>
                <linearGradient id="pf-doc-front" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#22304f" />
                    <stop offset="100%" stopColor="#151f33" />
                </linearGradient>
                <radialGradient id="pf-halo" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="rgba(33,194,200,0.5)" />
                    <stop offset="100%" stopColor="rgba(33,194,200,0)" />
                </radialGradient>
            </defs>

            {/* Back document — most faded, tilted right */}
            <g transform="translate(255 145) rotate(9) translate(-70 -90)">
                <rect width="140" height="180" rx="5" fill="url(#pf-doc)" stroke="rgba(33,194,200,0.15)" strokeWidth="1" />
                <rect x="16" y="24" width="70" height="4" rx="1.5" fill="rgba(33,194,200,0.35)" />
                <g fill="rgba(255,255,255,0.08)">
                    <rect x="16" y="40" width="105" height="2.5" rx="1" />
                    <rect x="16" y="50" width="85" height="2.5" rx="1" />
                    <rect x="16" y="60" width="100" height="2.5" rx="1" />
                    <rect x="16" y="80" width="55" height="2.5" rx="1" />
                    <rect x="16" y="90" width="90" height="2.5" rx="1" />
                </g>
            </g>

            {/* Middle document */}
            <g transform="translate(200 145) translate(-70 -90)">
                <rect width="140" height="180" rx="5" fill="url(#pf-doc)" stroke="rgba(33,194,200,0.25)" strokeWidth="1" />
                <rect x="16" y="24" width="70" height="4" rx="1.5" fill="rgba(33,194,200,0.55)" />
                <g fill="rgba(255,255,255,0.14)">
                    <rect x="16" y="40" width="105" height="2.5" rx="1" />
                    <rect x="16" y="50" width="85" height="2.5" rx="1" />
                    <rect x="16" y="60" width="100" height="2.5" rx="1" />
                    <rect x="16" y="80" width="55" height="2.5" rx="1" />
                    <rect x="16" y="90" width="90" height="2.5" rx="1" />
                    <rect x="16" y="100" width="70" height="2.5" rx="1" />
                    <rect x="16" y="120" width="100" height="2.5" rx="1" />
                </g>
            </g>

            {/* Front document — sharpest, tilted left */}
            <g transform="translate(150 155) rotate(-8) translate(-70 -90)">
                <rect width="140" height="180" rx="5" fill="url(#pf-doc-front)" stroke="rgba(33,194,200,0.45)" strokeWidth="1.25" />
                <rect x="16" y="24" width="70" height="4" rx="1.5" fill="#21c2c8" />
                <g fill="rgba(255,255,255,0.24)">
                    <rect x="16" y="40" width="105" height="2.5" rx="1" />
                    <rect x="16" y="50" width="85" height="2.5" rx="1" />
                    <rect x="16" y="60" width="100" height="2.5" rx="1" />
                    <rect x="16" y="80" width="55" height="2.5" rx="1" />
                    <rect x="16" y="90" width="90" height="2.5" rx="1" />
                    <rect x="16" y="100" width="70" height="2.5" rx="1" />
                    <rect x="16" y="120" width="95" height="2.5" rx="1" />
                    <rect x="16" y="130" width="70" height="2.5" rx="1" />
                    <rect x="16" y="150" width="60" height="2.5" rx="1" />
                </g>
            </g>

            {/* Verification badge — overlaps front document top-right */}
            <g transform="translate(215 78)">
                <circle cx="0" cy="0" r="40" fill="url(#pf-halo)" />
                <circle cx="0" cy="0" r="22" fill="#21c2c8" stroke="rgba(160,245,250,0.55)" strokeWidth="1.5" />
                <circle cx="0" cy="0" r="17" fill="none" stroke="rgba(11,18,32,0.35)" strokeWidth="1" strokeDasharray="2 3" />
                <path d="M -9 0 L -3 7 L 10 -7" stroke="#0b1220" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </g>
        </svg>
    );
}

const PROJECTS = [
    {
        id: 'pharmacy-license',
        title: 'Pharmacy License Compliance',
        role: 'Consultant',
        summary: 'Automated monthly license verification for pharmacy staff across every U.S. state board.',
        body: [
            "Compliance requires verifying that every pharmacist and technician's state license is active and free of disciplinary action — every month. Manually that meant staff visiting each state's board of pharmacy site, searching each licensee by name or number, reviewing the result for status and any penalty, and logging it by hand — a multi-day slog across dozens of different interfaces, each with its own quirks and formats.",
            'The system scrapes each state site on a schedule and uses an LLM to read the returned license records — flagging anything expired, penalized, or ambiguous — turning a recurring manual task into a scheduled job that produces a clean monthly compliance report.',
        ],
        stack: ['Python', 'AWS', 'LLM', 'Web Scraping', 'Compliance Automation'],
        Scene: PharmacyLicenseScene,
    },
    {
        id: 'court-mandated-education',
        title: 'Court-Mandated Education Platform',
        role: 'Sole engineer',
        summary: 'Online course platform for the court-mandated 12.5-hour DUI education program in Pennsylvania.',
        body: [
            "Pennsylvania courts require DUI offenders to complete a 12.5-hour state-approved alcohol highway safety course before certain penalties can be resolved. County DUI coordinators authorize each offender individually, and completion certificates have to flow back into the state's compliance workflow — historically handled entirely through in-person classes on a limited schedule.",
            'I designed and built the full online platform end to end — authorization intake from county coordinators, offender registration with in-course identity verification, the accredited PA-DOT curriculum delivered online, payments, a 30-day completion deadline enforced automatically, instant certificate generation on completion, and completion notices sent back to the authorizing facility. Sole engineer on the entire build; the platform runs in production serving offenders across Pennsylvania.',
        ],
        stack: ['Full-Stack Web', 'Payments', 'Certificate Automation', 'PA-DOT Compliance'],
        Scene: EducationPlatformScene,
    },
    {
        id: 'freight-pricing',
        title: 'Fuel-Aware Freight Pricing',
        role: 'Sole engineer',
        summary: 'Web platform and load-pricing engine for a national trucking company — quotes that stay profitable when fuel prices move.',
        body: [
            'For a carrier moving freight across the country, quoting a load is deceptively hard — the profitable rate depends on miles, weight, deadhead, and seasonality, but the swing factor is fuel. Diesel prices move daily and vary sharply by region, and a rate that pencils out on Monday can be a loss by Friday. Small carriers usually quote from spreadsheets or gut and eat the fuel risk on every job.',
            'I built the platform end to end — the customer-facing site, the internal tooling, and the core piece: a custom load-pricing engine that pulls real-time regional diesel prices and folds them into every quote alongside distance, load characteristics, and route. Dispatchers get a defensible number in seconds, and the numbers stay honest when the market moves underneath them.',
        ],
        stack: ['React', 'Vite', 'Real-Time Pricing', 'Fuel Price Integration', 'Logistics'],
        Scene: FreightPricingScene,
    },
    {
        id: 'freelance',
        title: 'Selected Freelance Work',
        role: 'Multiple clients',
        summary: 'Shorter engagements — data extraction, web scraping, and cloud advisory across a range of clients.',
        entries: [
            {
                title: 'PDF-to-SQL data pipeline',
                description: 'Extraction of quarterly PDF reports into a structured, SQL-ready workflow across multiple source formats and reporting groups — a recurring engagement across many quarterly deliveries.',
            },
            {
                title: 'Sales-outreach data enrichment',
                description: 'Scraped and structured 10–20K prospect records from a customer-facing web directory into a clean, deduplicated outreach dataset.',
            },
            {
                title: 'Bulk contact-list extraction',
                description: 'Extracted structured contact records (name, email, domain, company) from paginated search-tool results into a normalized CSV dataset.',
            },
            {
                title: 'AWS advisory: MS Access → cloud',
                description: 'Cost, architecture, and migration-path assessment for an MS Access-based SaaS product considering a move to AWS — including which layers to migrate first and per-tier trade-offs.',
            },
        ],
        stack: ['Python', 'Web Scraping', 'PDF Parsing', 'SQL', 'AWS', 'Data Automation'],
        Scene: FreelanceScene,
    },
];

function VisualPanel({ Scene }) {
    return (
        <div className="relative min-h-[240px] md:min-h-full flex items-center justify-center overflow-hidden bg-bg-base p-6">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(33,194,200,0.18),transparent_65%)]" aria-hidden="true"></div>
            <div
                className="absolute inset-0 opacity-50"
                aria-hidden="true"
                style={{
                    backgroundImage: 'radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)',
                    backgroundSize: '22px 22px',
                }}
            ></div>
            <Scene />
        </div>
    );
}

function ProjectCard({ project, index }) {
    const { Scene } = project;
    const num = String(index + 1).padStart(2, '0');
    const visualFirst = index % 2 === 0;

    return (
        <article className="bg-bg-card border border-border-subtle rounded-2xl overflow-hidden shadow-card hover:border-accent/40 hover:shadow-cardHover transition-all">
            <div className="grid md:grid-cols-2">
                <div className={visualFirst ? '' : 'md:order-last'}>
                    <VisualPanel Scene={Scene} />
                </div>

                <div className="p-6 sm:p-8 md:p-10 flex flex-col justify-center">
                    <div className="text-[10px] sm:text-xs font-mono uppercase tracking-[0.2em] text-ink-muted mb-3">
                        <span className="text-accent">{num}</span> · {project.role}
                    </div>

                    <h2 className="text-2xl sm:text-3xl font-bold text-ink-primary leading-tight">
                        {project.title}
                    </h2>

                    <p className="mt-3 text-accent text-sm sm:text-base leading-snug">
                        {project.summary}
                    </p>

                    {project.body && (
                        <div className="mt-5 space-y-3">
                            {project.body.map((paragraph, i) => (
                                <p key={i} className="text-ink-secondary text-sm sm:text-base leading-relaxed">{paragraph}</p>
                            ))}
                        </div>
                    )}

                    {project.entries && (
                        <ul className="mt-5 space-y-4 border-l-2 border-accent/30 pl-5">
                            {project.entries.map((e) => (
                                <li key={e.title}>
                                    <div className="text-ink-primary font-semibold text-sm sm:text-base">{e.title}</div>
                                    <p className="mt-1 text-ink-secondary text-sm leading-relaxed">{e.description}</p>
                                </li>
                            ))}
                        </ul>
                    )}

                    <div className="mt-6 flex flex-wrap gap-1.5">
                        {project.stack.map((t) => (
                            <span key={t} className="cap-chip">{t}</span>
                        ))}
                    </div>
                </div>
            </div>
        </article>
    );
}

class Portfolio extends Component {
    render() {
        return (
            <div className="wrap">
                <section className="py-20 px-4 sm:px-6">
                    <div className="max-w-5xl mx-auto">
                        <div className="text-center mb-12">
                            <h1 className="text-4xl sm:text-5xl font-bold text-ink-primary">My Work</h1>
                            <div className="mx-auto mt-3 h-1 w-12 bg-accent rounded-full"></div>
                            <p className="mt-4 text-ink-secondary max-w-2xl mx-auto">A selection of independent projects and shipped client work.</p>
                        </div>

                        <div className="space-y-8">
                            {PROJECTS.map((p, i) => <ProjectCard key={p.id} project={p} index={i} />)}
                        </div>
                    </div>
                </section>
            </div>
        );
    }
}
export default Portfolio;
